// src/chat/chatSocket.js
// Sohbet ve arama sinyalleşmesi için tek socket bağlantısı.
//
// Canlı maç kanalından ayrı tutulur: o kanal maç odalarına anonim bağlanır,
// bu bağlantı ise kimlikle (authToken çerezi + Bearer jeton) açılır ve
// sunucu socket'i user:<id> odasına alır. Bağlantı sayaçla (retain/release)
// yaşar; son abone ayrılınca kapanır.
import { io } from "socket.io-client";
import { AUTH_TOKEN_KEY } from "../api/api";

const SOCKET_URL = process.env.REACT_APP_LIVE_SOCKET_URL || process.env.REACT_APP_API_URL || "https://elitlig-api-88a866b7a4da.herokuapp.com";

// Gelen kutusu (InboxDock) okunmamış sayacını aldığında pencereye yayılan olay;
// üye paneli menü rozetini ayrı yoklama yapmadan buradan günceller.
export const CHAT_UNREAD_EVENT = "elitlig:chat-unread";

export const CHAT_EVENTS = Object.freeze({
  READY: "chat:ready",
  MESSAGE: "chat:message",
  CONVERSATION: "chat:conversation",
  READ: "chat:read",
  TYPING: "chat:typing",
  DELETED: "chat:deleted",
  UPDATED: "chat:updated",
  ADMIN_INBOX: "chat:admin-inbox",
  CALL_INCOMING: "call:incoming",
  CALL_ACCEPTED: "call:accepted",
  CALL_ENDED: "call:ended",
  CALL_ICE: "call:ice",
  CALL_STATE: "call:state",
});

const readToken = () => {
  try {
    const stored = localStorage.getItem(AUTH_TOKEN_KEY) || localStorage.getItem("token");
    if (stored) return stored;
    return JSON.parse(localStorage.getItem("user") || "null")?.token || null;
  } catch { return null; }
};

let socket = null;
let refs = 0;
const statusListeners = new Set();

function notifyStatus(status) { statusListeners.forEach((fn) => { try { fn(status); } catch { /* dinleyici hatası bağlantıyı etkilemez */ } }); }

function connect() {
  if (socket) return socket;
  socket = io(SOCKET_URL, {
    transports: ["websocket", "polling"],
    withCredentials: true,
    auth: (cb) => cb({ token: readToken() }),
    reconnection: true,
    reconnectionAttempts: Infinity,
    reconnectionDelay: 1000,
    reconnectionDelayMax: 30000,
    randomizationFactor: 0.5,
    timeout: 10000,
  });
  socket.on("connect", () => notifyStatus("connected"));
  socket.on("disconnect", () => notifyStatus("disconnected"));
  socket.on("connect_error", () => notifyStatus("reconnecting"));
  return socket;
}

export function retainChatSocket() {
  refs += 1;
  return connect();
}

export function releaseChatSocket() {
  refs = Math.max(0, refs - 1);
  if (refs === 0 && socket) {
    socket.removeAllListeners();
    socket.disconnect();
    socket = null;
  }
}

export function getChatSocket() { return socket; }

export function onChatStatus(fn) {
  statusListeners.add(fn);
  return () => statusListeners.delete(fn);
}

/** Olaya abone olur; kaldırma fonksiyonu döner. */
export function onChatEvent(event, handler) {
  const s = connect();
  s.on(event, handler);
  return () => { if (socket) socket.off(event, handler); };
}

/** Ack bekleyen istek; socket bağlı değilse reddeder (çağıran REST'e düşer). */
export function emitWithAck(event, payload, timeoutMs = 8000) {
  return new Promise((resolve, reject) => {
    const s = socket;
    if (!s || !s.connected) { reject(new Error("SOCKET_OFFLINE")); return; }
    const timer = setTimeout(() => reject(new Error("SOCKET_TIMEOUT")), timeoutMs);
    s.emit(event, payload, (response) => {
      clearTimeout(timer);
      if (response && response.ok === false) {
        const error = new Error(response.message || "İşlem başarısız.");
        error.code = response.code;
        error.response = { data: { code: response.code, message: response.message } };
        reject(error);
      } else resolve(response || {});
    });
  });
}

export function emitTyping(conversationId, typing = true) {
  if (socket?.connected) socket.emit("chat:typing", { conversation_id: conversationId, typing });
}
